import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router";
import { useSearchParams } from "react-router-dom";

function useQueryState<T extends string>(
    key: string,
    defaultValue?: T
): [T, Dispatch<SetStateAction<T>>] {
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();

    const getParam = useCallback((): T => {
        const param = searchParams.get(key);
        if (param) {
            return decodeURIComponent(param) as T;
        }
        return (defaultValue ?? '') as T;
    }, [defaultValue, key, searchParams]);

    const [value, setValue] = useState<T>(getParam);

    const update: Dispatch<SetStateAction<T>> = useCallback((newValue) => {
        const params = new URLSearchParams(window.location.search);
        const currentParam = params.get(key);
        const currentValue = currentParam ? decodeURIComponent(currentParam) as T : (defaultValue ?? '') as T;

        const resolved =
            typeof newValue === "function"
                ? (newValue as (prev: T) => T)(currentValue)
                : newValue;

        if (resolved) {
            params.set(key, encodeURIComponent(resolved));
        } else {
            params.delete(key);
        }

        setSearchParams(params, { replace: true });
        setValue(resolved);
    }, [key, defaultValue, setSearchParams]);

    useEffect(() => {
        setValue(getParam());
    }, [location.search, getParam]);

    return [value, update];
}

export default useQueryState;
